/**
 * Region Card Component
 * Displays a single region with status, country info, and factory/city counts
 */

import { useTranslation, useIsRTL } from '@spcc/core'
import {
  Card,
  Badge,
  Button,
  MapPin,
  Globe,
  Factory,
  Building2,
  Settings,
  CheckCircle2,
  XCircle,
  cn,
} from '@spcc/uikit'
import type { RegionListItem } from '../../domain/types'
import { REGION_STATUS_CONFIG, T_KEYS } from '../../domain/constants'

// ============================================
// Helper Functions
// ============================================

function formatDate(dateString: string, locale: string): string {
  try {
    return new Date(dateString).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '-'
  }
}

function getStatusKey(isActive: boolean): 'active' | 'inactive' {
  return isActive ? 'active' : 'inactive'
}

// ============================================
// Types
// ============================================

export interface RegionCardProps {
  region: RegionListItem
  onSelect?: (region: RegionListItem) => void
  onSettings?: (region: RegionListItem) => void
}

// ============================================
// Component
// ============================================

export function RegionCard({
  region,
  onSelect,
  onSettings,
}: RegionCardProps) {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar' // Keep for content selection (Arabic text vs English)

  const statusKey = getStatusKey(region.isActive)
  const statusConfig = REGION_STATUS_CONFIG[statusKey]

  return (
    <Card
      className={cn(
        'glass-card p-6',
        onSelect && 'cursor-pointer hover:ring-2 hover:ring-primary/20'
      )}
      onClick={() => onSelect?.(region)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-foreground truncate">
            {region.name}
          </h3>
          <p className="text-sm text-muted-foreground font-mono">{region.code}</p>
        </div>
        <div className="flex items-center gap-2 ms-3">
          <Badge
            variant="secondary"
            className={cn('gap-1', statusConfig.bgClass, statusConfig.textClass)}
          >
            {region.isActive ? (
              <CheckCircle2 className="w-3 h-3" />
            ) : (
              <XCircle className="w-3 h-3" />
            )}
            {t(region.isActive ? 'regions.active' : 'regions.inactive')}
          </Badge>
          {onSettings && (
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={(e) => {
                e.stopPropagation()
                onSettings(region)
              }}
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Location Info */}
      <div className="space-y-2 mb-4 min-h-[72px]">
        {/* Country */}
        <div className="flex items-center gap-2 text-sm">
          <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
          {region.countryName ? (
            <span className="text-foreground">
              {region.countryCode && (
                <span className="text-muted-foreground me-1">[{region.countryCode}]</span>
              )}
              {region.countryName}
            </span>
          ) : (
            <span className="text-muted-foreground/60 italic">
              {isArabic ? 'لم يتم تحديد البلد' : 'No country assigned'}
            </span>
          )}
        </div>

        {/* Factories Count */}
        <div className="flex items-center gap-2 text-sm">
          <Factory className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-foreground">
            {region.factoryCount > 0 ? (
              <>
                <span className="font-semibold">{region.factoryCount}</span>
                {' '}
                {isArabic ? 'مصنع' : region.factoryCount === 1 ? 'factory' : 'factories'}
                {region.factoryNames && region.factoryNames.length > 0 && (
                  <span className="text-muted-foreground ms-1">
                    ({region.factoryNames.slice(0, 2).join(', ')}
                    {region.factoryNames.length > 2 && '...'})
                  </span>
                )}
              </>
            ) : (
              <span className="text-muted-foreground/60 italic">
                {t('regions.card.noFactories')}
              </span>
            )}
          </span>
        </div>

        {/* Cities Count */}
        <div className="flex items-center gap-2 text-sm">
          <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-foreground">
            {region.cityCount > 0 ? (
              <>
                <span className="font-semibold">{region.cityCount}</span>
                {' '}
                {isArabic ? 'مدينة' : region.cityCount === 1 ? 'city' : 'cities'}
              </>
            ) : (
              <span className="text-muted-foreground/60 italic">
                {t('regions.card.noCities')}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Footer - Timestamps */}
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border/50">
        <span>
          {isArabic ? 'تم الإنشاء' : 'Created'}: {formatDate(region.createdAt, i18n.language)}
        </span>
        {region.updatedAt && region.updatedAt !== region.createdAt && (
          <span>
            {isArabic ? 'تم التحديث' : 'Updated'}: {formatDate(region.updatedAt, i18n.language)}
          </span>
        )}
      </div>
    </Card>
  )
}
